import * as React from "react";
import { DropdownMenu as DropdownMenuPrimitive } from "radix-ui";
import { useNavigate as useRouter } from "./router";
import { cn } from "./utils";
import { Elevated } from "./elevated";
import { COUNT_BUBBLE } from "./count";
import type { ContextMenuItem } from "./context-menu";

interface DropdownMenuProps {
  items: ContextMenuItem[];
  children: React.ReactNode;
  asChild?: boolean;
  widthPx?: number;
  align?: "start" | "center" | "end";
  side?: "top" | "right" | "bottom" | "left";
  /** Notified as the menu opens/closes — lets a host keep surrounding chrome
   *  open while the (portaled) menu holds focus (e.g. the dock's results panel). */
  onOpenChange?: (open: boolean) => void;
}

/**
 * Click-to-open menu sharing the same item shape as `ContextMenu`. Used as
 * the explicit affordance for sidebar product items (the "..." button) since
 * right-click discoverability is poor and Chrome can swallow it.
 */
export function DropdownMenu({
  items,
  children,
  asChild = true,
  widthPx = 200,
  align = "end",
  side = "bottom",
  onOpenChange,
}: DropdownMenuProps) {
  const router = useRouter();

  return (
    <DropdownMenuPrimitive.Root onOpenChange={onOpenChange}>
      <DropdownMenuPrimitive.Trigger asChild={asChild}>
        {children}
      </DropdownMenuPrimitive.Trigger>
      <DropdownMenuPrimitive.Portal>
        {/* Heavier shadow + hairline border so the menu reads as lifted off the
            near-white canvas, not flush with it (surface bg alone is too close). */}
        <Elevated offset={2} shadowLevel={5} asChild>
        <DropdownMenuPrimitive.Content
          align={align}
          side={side}
          sideOffset={4}
          className={cn(
            // z-[90]: above Dialog/Sheet (z-[80]); see packages/ui/src/dialog.tsx z-ledger.
            "z-[90] overflow-hidden rounded-lg border border-border p-1 text-foreground",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
            "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
          )}
          style={{ minWidth: widthPx }}
        >
          {items.map((item, i) => {
            if ("type" in item && item.type === "separator") {
              return (
                <DropdownMenuPrimitive.Separator
                  key={`sep-${i}`}
                  className="my-1 h-px bg-border"
                />
              );
            }
            if ("type" in item && item.type === "label") {
              return (
                <DropdownMenuPrimitive.Label
                  key={`lab-${i}-${item.label}`}
                  className="px-2 py-1.5 type-body text-muted-foreground"
                >
                  {item.label}
                </DropdownMenuPrimitive.Label>
              );
            }
            return (
              <DropdownMenuPrimitive.Item
                key={`it-${i}-${item.label}`}
                disabled={item.disabled}
                onSelect={(e) => {
                  if (item.disabled) {
                    e.preventDefault();
                    return;
                  }
                  if (item.href) {
                    if (item.newTab) {
                      window.open(item.href, "_blank", "noopener,noreferrer");
                    } else {
                      router.push(item.href);
                    }
                    return;
                  }
                  item.onSelect?.();
                }}
                className={cn(
                  "relative flex cursor-pointer select-none items-center gap-2 rounded px-2 py-1.5 type-body",
                  "outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus-visible:ring-0",
                  "data-[highlighted]:bg-foreground/[0.06] data-[highlighted]:text-foreground",
                  "data-[disabled]:pointer-events-none data-[disabled]:opacity-50 data-[disabled]:cursor-not-allowed",
                  // Current selection reads as color (selected surface + foreground
                  // weight), not a check icon — keeps labels flush-left.
                  item.selected && "bg-selected font-medium text-foreground",
                  item.destructive &&
                    "text-destructive data-[highlighted]:text-destructive",
                )}
              >
                {item.icon ? (
                  <span className="inline-flex h-3.5 w-3.5 shrink-0 items-center justify-center">
                    {item.icon}
                  </span>
                ) : null}
                <span className="flex-1 truncate">{item.label}</span>
                {item.shortcut ? (
                  <span className="ml-auto type-body tracking-widest text-muted-foreground">
                    {item.shortcut}
                  </span>
                ) : null}
                {typeof item.count === "number" && item.count > 0 ? (
                  <span className={cn(COUNT_BUBBLE, "ml-auto shrink-0 type-subtext")}>
                    {item.count > 99 ? "99+" : item.count}
                  </span>
                ) : null}
              </DropdownMenuPrimitive.Item>
            );
          })}
        </DropdownMenuPrimitive.Content>
        </Elevated>
      </DropdownMenuPrimitive.Portal>
    </DropdownMenuPrimitive.Root>
  );
}
